import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LifeBuoy, Mail, MessageCircle, Clock, ExternalLink, BookOpen, FileDown,
  ChevronDown, Upload, ShieldAlert, RotateCcw, Boxes, ArrowRight, Truck, Receipt,
  LayoutDashboard, Package, BarChart3, FileText,
} from "lucide-react";

// Update these with your real support channels.
const SUPPORT_WHATSAPP = "19299001101"; // +1 929-900-1101
const SUPPORT_EMAIL = "3pl@boxhero.com";

// Staff/employee ops guides live on the Training hub.
const STAFF_GUIDES = [
  { key: "receiving", icon: Boxes, title: "Receiving inventory", desc: "Inbound POs, locations, scanning, verify & export to ShipStation." },
  { key: "claims", icon: ShieldAlert, title: "Handling claims", desc: "Review, work carrier statuses and apply credit." },
  { key: "returns", icon: RotateCcw, title: "Processing returns", desc: "Receive, inspect, photograph and set dispositions." },
];

function downloadPoTemplate() {
  const rows = [
    ["SKU", "Barcode", "Expected Quantity", "Product Title", "Variant", "Vendor", "Proposed Location"],
    ["ABC-123", "012345678905", "50", "Classic Tee", "Medium / Blue", "Acme Mfg", ""],
    ["ABC-124", "012345678912", "30", "Classic Tee", "Large / Blue", "Acme Mfg", ""],
  ];
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "BoxHero_PO_Template.csv"; a.click();
  URL.revokeObjectURL(url);
}

// Customer-facing guides (shown inline — customers don't have the staff Training hub).
const CLIENT_GUIDES = [
  {
    id: "how-it-works", icon: Truck, title: "How BoxHero works",
    summary: "The big picture, start to finish.",
    steps: [
      "You send your inventory to our warehouse and tell us what's coming with a Purchase Order (PO).",
      "We receive and count every unit, flag anything damaged, and store it in labelled bins.",
      "When your shoppers order, we pick, pack and ship — and you track everything right here.",
      "Returns come back to us; we inspect each item and restock what's resellable.",
    ],
  },
  {
    id: "dashboard", icon: LayoutDashboard, title: "Read your dashboard",
    summary: "Your at-a-glance health check.",
    steps: [
      "The Dashboard opens first and summarizes orders shipped, units, and recent activity for your store.",
      "Use the store switcher (top-right) if you manage more than one brand — every page follows that choice.",
      "Cards link straight into the detail pages (orders, returns, claims) so you can drill in with one click.",
      "Numbers update as we ship, receive and process returns throughout the day.",
    ],
    action: { label: "Go to Dashboard", to: "/" },
  },
  {
    id: "orders-tracking", icon: Package, title: "Find orders & track shipments",
    summary: "Search any order and follow the parcel.",
    steps: [
      "Open Orders & Shipments to see every order we've shipped for you.",
      "Search by order number, customer or tracking number, and filter by date.",
      "Click an order to see its items, ship date, carrier and live tracking status.",
      "If something looks lost or damaged, you can file a claim right from the order.",
    ],
    action: { label: "Go to Orders & Shipments", to: "/orders-shipments" },
  },
  {
    id: "upload-po", icon: Upload, title: "Send us inbound inventory (upload a PO)",
    summary: "Tell us what's arriving so we can receive it accurately.",
    steps: [
      "Download the PO template below and fill it in — one row per product.",
      "Required columns: SKU, Barcode/UPC, and Expected Quantity. Product title, variant and vendor are optional but helpful.",
      "Add a Location column if you already know where items should go — we'll pre-assign those bins.",
      "Go to Inbound POs → Upload, choose your file, map the columns, review the preview, and submit.",
    ],
    template: true,
    action: { label: "Go to Inbound POs", to: "/inventory" },
  },
  {
    id: "returns", icon: RotateCcw, title: "Track your returns & set dispositions",
    summary: "See what came back and tell us what to do with it.",
    steps: [
      "Open the Returns page to see every processed return for your store, with photos of each item.",
      "For each returned item, pick a disposition: restock, hold, send back, donate or dispose.",
      "Only items we physically received back appear here — nothing you didn't get is listed.",
      "You'll also get a daily 6 PM summary email on days returns were processed.",
    ],
    action: { label: "Go to Returns", to: "/returns" },
  },
  {
    id: "file-claim", icon: ShieldAlert, title: "File a claim (lost or damaged shipment)",
    summary: "Get credit when a shipment goes wrong.",
    steps: [
      "Open Orders & Shipments and find the affected order.",
      "Click 'File claim' and pick the reason: lost, never delivered, lost after delivery, no movement, or damaged.",
      "Enter the wholesale and sold values, and attach photos or screenshots as evidence.",
      "Track the status timeline (Received → Filed → Under review → Approved → Credit applied) on your Claims card.",
    ],
    action: { label: "Go to Orders & Shipments", to: "/orders-shipments" },
  },
  {
    id: "reports", icon: BarChart3, title: "Run & export reports",
    summary: "Pull your shipped orders for any date range.",
    steps: [
      "Open Reports and choose a start and end ship date.",
      "Click Run report to see every order shipped in that window, with units and costs.",
      "Export to CSV or Excel for your own bookkeeping with one click.",
      "Reports are per store — pick a specific store from the switcher first.",
    ],
    action: { label: "Go to Reports", to: "/reports" },
  },
  {
    id: "billing", icon: Receipt, title: "Billing & invoices",
    summary: "Find your charges and statements.",
    steps: [
      "Billing shows your usage and charges; Invoices lists your statements to download.",
      "Charges are based on ship date. Questions about a specific charge? Message us — we're happy to walk through it.",
    ],
    action: { label: "Go to Billing", to: "/billing" },
  },
  {
    id: "invoices", icon: FileText, title: "Download your invoices",
    summary: "Grab statements for your records.",
    steps: [
      "Open Invoices to see every statement we've issued for your store.",
      "Each invoice shows the billing period, totals and a downloadable copy.",
      "Cross-check the line items against your Billing usage for the same period.",
    ],
    action: { label: "Go to Invoices", to: "/invoices" },
  },
];

function GuideAccordion({ items }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(items[0]?.id || null);
  return (
    <div className="space-y-3" data-testid="client-guides">
      {items.map((g) => {
        const isOpen = open === g.id;
        return (
          <div key={g.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`client-guide-${g.id}`}>
            <button onClick={() => setOpen(isOpen ? null : g.id)} data-testid={`client-guide-toggle-${g.id}`} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50">
              <div className="w-10 h-10 rounded-xl bg-bh-blue text-white flex items-center justify-center shrink-0"><g.icon className="w-5 h-5" /></div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{g.title}</h3>
                <p className="text-sm text-slate-500 truncate">{g.summary}</p>
              </div>
              <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <div className="px-5 pb-5 pt-1">
                <ol className="space-y-2">
                  {g.steps.map((s, i) => (
                    <li key={`${g.id}-${i}`} className="flex items-start gap-3 text-sm text-slate-700">
                      <span className="w-5 h-5 rounded-full bg-orange-100 text-bh-orange text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                      {s}
                    </li>
                  ))}
                </ol>
                <div className="flex flex-wrap gap-2 mt-4">
                  {g.template && (
                    <button onClick={downloadPoTemplate} data-testid="download-po-template" className="inline-flex items-center gap-1.5 bg-bh-orange hover:bg-bh-orange-dark text-bh-navy-deep font-bold text-sm px-4 py-2 rounded-md"><FileDown className="w-4 h-4" /> Download PO template (CSV)</button>
                  )}
                  {g.action && (
                    <button onClick={() => navigate(g.action.to)} data-testid={`client-guide-goto-${g.id}`} className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-sm px-4 py-2 rounded-md">{g.action.label} <ArrowRight className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Support() {
  const { user, account, isStaff, viewAs } = useAuth();
  const navigate = useNavigate();
  const impersonating = viewAs && viewAs !== "all";
  const showStaff = isStaff && !impersonating; // acting-as-customer previews the client guides
  const company = user?.company_name || user?.account_name || "";
  const greeting = encodeURIComponent(`Hi BoxHero team, this is ${user?.name || "a customer"}${company ? ` from ${company}` : ""}. I need help with:`);
  const groupUrl = account?.whatsapp_group_url;
  const waLink = groupUrl || `https://wa.me/${SUPPORT_WHATSAPP}?text=${greeting}`;
  const mailLink = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Support request${company ? ` — ${company}` : ""}`)}`;

  return (
    <div data-testid="support-page" className="space-y-8 max-w-4xl">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center"><LifeBuoy className="w-6 h-6" /></div>
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Support & Guides</h1>
          <p className="text-slate-500 mt-0.5">{showStaff ? "Team how-to guides plus a direct line to us." : "Step-by-step help, templates, and a direct line to our team."}</p>
        </div>
      </div>

      {/* Guides */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-bh-blue" />
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{showStaff ? "Team how-to guides" : "Guides & resources"}</h2>
        </div>

        {showStaff ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="staff-guides">
            {STAFF_GUIDES.map((g) => (
              <button key={g.key} onClick={() => navigate(`/training?topic=${g.key}`)} data-testid={`staff-guide-${g.key}`} className="text-left bg-white border border-slate-200 hover:border-bh-blue rounded-2xl p-5 transition-colors group">
                <div className="w-11 h-11 rounded-xl bg-bh-blue text-white flex items-center justify-center mb-3"><g.icon className="w-6 h-6" /></div>
                <h3 className="font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{g.title}</h3>
                <p className="text-sm text-slate-500 mt-1">{g.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-bh-blue">Open guide <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
              </button>
            ))}
          </div>
        ) : (
          <GuideAccordion items={CLIENT_GUIDES} />
        )}
      </div>

      {/* Contact */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-emerald-600" />
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Still need a hand? Talk to us</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <a href={waLink} target="_blank" rel="noreferrer" data-testid="support-whatsapp" className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-emerald-400 hover:shadow-lg transition-all">
            <div className="w-12 h-12 rounded-xl bg-emerald-500 text-white flex items-center justify-center mb-4 group-hover:scale-105 transition-transform"><MessageCircle className="w-6 h-6" /></div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Chat on WhatsApp</h2>
            <p className="text-sm text-slate-500 mt-1">{groupUrl
              ? "Support starts here — fast and easy, no tickets. Join your dedicated BoxHero group and chat directly with our team."
              : "Fastest way to reach us. Opens a direct chat with your details pre-filled."}</p>
            <span className="inline-flex items-center gap-1.5 mt-4 text-emerald-600 font-semibold text-sm">{groupUrl ? "Join your group" : "Start chat"} <ExternalLink className="w-4 h-4" /></span>
          </a>
          <a href={mailLink} data-testid="support-email" className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-400 hover:shadow-lg transition-all">
            <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center mb-4 group-hover:scale-105 transition-transform"><Mail className="w-6 h-6" /></div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Email us</h2>
            <p className="text-sm text-slate-500 mt-1">Prefer email? We&apos;ll get back to you, usually within one business day.</p>
            <span className="inline-flex items-center gap-1.5 mt-4 text-blue-600 font-semibold text-sm">{SUPPORT_EMAIL} <ExternalLink className="w-4 h-4" /></span>
          </a>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 flex items-start gap-3">
          <Clock className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
          <p className="text-sm text-slate-600">Support hours: <strong className="text-slate-800">Mon–Fri, 9am–6pm ET</strong>. For urgent shipment issues, WhatsApp gets the quickest response.</p>
        </div>
      </div>
    </div>
  );
}
